import React, { useState, useRef } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import API, { resolvePhoto } from '../../utils/api';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import {
  ChevronRight, ChevronLeft, CheckCircle, Upload, X,
  Image, Type, Info, Megaphone,
} from 'lucide-react';
import './SubmitAd.css';
import { useSiteSettings } from '../../context/SiteSettingsContext';

const STEPS = [
  { n: 1, label: 'Data Pengiklan' },
  { n: 2, label: 'Materi Iklan' },
  { n: 3, label: 'Konfirmasi' },
];

const GUIDE_ITEMS = [
  {
    icon: <Image size={18} />,
    title: 'Format Gambar',
    items: [
      'Tipe file: JPG, PNG, atau WebP',
      'Ukuran maksimal: 2 MB per file',
      'Resolusi minimal: 1200 x 200 piksel',
      'Rasio yang disarankan: 6:1 (contoh: 1200 x 200 atau 1800 x 300)',
      'Gunakan resolusi tinggi agar tampil tajam di semua layar',
    ],
  },
  {
    icon: <Type size={18} />,
    title: 'Iklan Teks',
    items: [
      'Judul maksimal 60 karakter',
      'Teks badan maksimal 120 karakter',
      'Teks harus informatif dan tidak menyesatkan',
      'Sertakan ajakan bertindak yang jelas (contoh: Daftar Sekarang, Pelajari Lebih Lanjut)',
    ],
  },
  {
    icon: <Info size={18} />,
    title: 'Kebijakan Konten',
    items: [
      'Iklan wajib sesuai dengan nilai platform: inovasi, pendidikan, dan dampak sosial',
      'Tidak diperkenankan: konten judi, pinjaman ilegal, hoaks, atau SARA',
      'Tim redaksi berhak menolak pengajuan yang tidak memenuhi standar editorial',
      'Iklan yang disetujui akan tayang di bagian atas halaman utama',
    ],
  },
];

export default function SubmitAd() {
  const { settings } = useSiteSettings();
  const sn = settings.siteName;
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0 = panduan, 1/2/3 = form
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    companyName: '',
    contactName: '',
    contactEmail: '',
    contactPhone: '',
    adType: 'image',
    title: '',
    bodyText: '',
    imageUrl: '',
    imageAlt: '',
    linkUrl: '',
    linkLabel: '',
    startDate: '',
    endDate: '',
    notes: '',
  });

  const up = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const step1OK =
    form.companyName.trim() &&
    form.contactName.trim() &&
    form.contactEmail.trim() &&
    form.contactPhone.trim();

  const step2OK =
    form.linkUrl.trim() &&
    form.startDate &&
    form.endDate &&
    (form.adType === 'image' ? form.imageUrl : form.bodyText.trim());

  const uploadImage = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('image', file);
    try {
      const { data } = await API.post('/ads/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      up('imageUrl', data.url);
      toast.success('Gambar berhasil diunggah');
    } catch {
      toast.error('Gagal mengunggah gambar');
    }
    setUploading(false);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await API.post('/ads', form);
      setDone(true);
      toast.success('Pengajuan iklan berhasil dikirim!');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Gagal mengajukan iklan');
    }
    setSaving(false);
  };

  // Fitur pasang iklan dinonaktifkan admin
  if (settings.featureAdSubmission === false) {
    return (
      <DashboardLayout title="Pasang Iklan" subtitle="">
        <div className="ki-card" style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--ki-text-muted)', fontSize: 14 }}>
          Fitur pasang iklan sedang tidak tersedia saat ini.
        </div>
      </DashboardLayout>
    );
  }

  // Sukses
  if (done) {
    return (
      <DashboardLayout title="Iklan Terkirim" subtitle="">
        <div className="submit-ad-success">
          <div className="submit-ad-success-icon">
            <CheckCircle size={36} color="#059669" />
          </div>
          <h2 className="submit-ad-success-title">Pengajuan Iklan Terkirim</h2>
          <p className="submit-ad-success-desc">
            Tim redaksi akan meninjau pengajuan Anda dan menghubungi Anda melalui email atau nomor
            telepon yang tercantum. Proses peninjauan membutuhkan waktu 1 sampai 3 hari kerja.
          </p>
          <div className="submit-ad-success-actions">
            <button className="btn btn-primary" onClick={() => navigate('/iklan-saya')}>
              Lihat Iklan Saya
            </button>
            <button className="btn btn-secondary" onClick={() => {
              setDone(false); setStep(0);
              setForm({
                companyName: '', contactName: '', contactEmail: '', contactPhone: '',
                adType: 'image', title: '', bodyText: '', imageUrl: '', imageAlt: '',
                linkUrl: '', linkLabel: '', startDate: '', endDate: '', notes: '',
              });
            }}>
              Ajukan Iklan Lain
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Panduan
  if (step === 0) {
    return (
      <DashboardLayout title="Pasang Iklan" subtitle={`Jangkau pembaca ${sn} dengan iklan Anda`}>
        <div className="submit-ad-wrap">
          {/* Header panduan */}
          <div className="ad-guide-header">
            <div className="ad-guide-header-icon">
              <Megaphone size={22} />
            </div>
            <div>
              <div className="ad-guide-header-title">Panduan Pemasangan Iklan</div>
              <div className="ad-guide-header-sub">
                Baca ketentuan berikut sebelum mengajukan iklan agar proses persetujuan berjalan lancar.
              </div>
            </div>
          </div>

          {/* Grid panduan */}
          <div className="ad-guide-grid">
            {GUIDE_ITEMS.map(g => (
              <div key={g.title} className="ad-guide-card">
                <div className="ad-guide-card-head">
                  <span className="ad-guide-card-icon">{g.icon}</span>
                  <span className="ad-guide-card-title">{g.title}</span>
                </div>
                <ul className="ad-guide-list">
                  {g.items.map(item => (
                    <li key={item}>{item}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>

          {/* Spesifikasi ukuran visual */}
          <div className="ad-guide-spec">
            <div className="ad-guide-spec-title">Contoh Proporsi Banner Iklan</div>
            <div className="ad-guide-spec-preview">
              <div className="ad-guide-spec-bar">
                <span>1200 x 200 px</span>
              </div>
            </div>
            <div className="ad-guide-spec-note">
              Banner ditampilkan penuh di bagian atas halaman utama dalam container selebar 1100 piksel.
              Gunakan desain horizontal yang tetap terbaca di layar kecil.
            </div>
          </div>

          {/* Alur pengajuan */}
          <div className="ad-guide-flow">
            <div className="ad-guide-flow-title">Alur Pengajuan</div>
            <div className="ad-guide-flow-steps">
              {[
                { n: '01', label: 'Isi formulir', desc: 'Lengkapi data pengiklan dan unggah materi iklan.' },
                { n: '02', label: 'Tinjauan redaksi', desc: 'Tim kami meninjau kesesuaian konten dalam 1 sampai 3 hari kerja.' },
                { n: '03', label: 'Konfirmasi dan tayang', desc: 'Setelah disetujui, iklan akan tayang sesuai tanggal yang dipilih.' },
              ].map((s, i, arr) => (
                <React.Fragment key={s.n}>
                  <div className="ad-flow-step">
                    <div className="ad-flow-step-n">{s.n}</div>
                    <div className="ad-flow-step-label">{s.label}</div>
                    <div className="ad-flow-step-desc">{s.desc}</div>
                  </div>
                  {i < arr.length - 1 && <div className="ad-flow-arrow">›</div>}
                </React.Fragment>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button className="btn btn-primary" onClick={() => setStep(1)}>
              Mulai Pengajuan <ChevronRight size={16} />
            </button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Step indicator
  const StepBar = () => (
    <div className="submit-ad-stepbar">
      {STEPS.map((s, i) => {
        const isActive = step === s.n;
        const isDone = step > s.n;
        return (
          <React.Fragment key={s.n}>
            <div className="submit-ad-step">
              <div className={`submit-ad-step-circle ${isDone ? 'done' : ''} ${isActive ? 'active' : ''}`}>
                {isDone ? '✓' : i + 1}
              </div>
              <span className={`submit-ad-step-label ${isActive || isDone ? 'active' : ''}`}>{s.label}</span>
            </div>
            {i < STEPS.length - 1 && (
              <div className={`submit-ad-step-line ${isDone ? 'done' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <DashboardLayout title="Pasang Iklan" subtitle="Lengkapi formulir pengajuan iklan">
      <div className="submit-ad-wrap">
        <StepBar />

        {/* Step 1: Data pengiklan */}
        {step === 1 && (
          <div className="ki-card">
            <div className="submit-ad-card-title">Data Pengiklan</div>
            <div className="alert alert-info" style={{ marginBottom: 20 }}>
              Informasi ini digunakan untuk keperluan komunikasi dan tidak ditampilkan secara publik.
            </div>
            <div className="submit-ad-grid-2">
              <div className="form-group">
                <label className="form-label">Nama Perusahaan atau Merek *</label>
                <input className="form-input" value={form.companyName}
                  onChange={e => up('companyName', e.target.value)}
                  placeholder="Contoh: Karya Digital Indonesia" />
              </div>
              <div className="form-group">
                <label className="form-label">Nama Kontak *</label>
                <input className="form-input" value={form.contactName}
                  onChange={e => up('contactName', e.target.value)}
                  placeholder="Nama lengkap penanggung jawab" />
              </div>
              <div className="form-group">
                <label className="form-label">Email Kontak *</label>
                <input className="form-input" type="email" value={form.contactEmail}
                  onChange={e => up('contactEmail', e.target.value)}
                  placeholder="email@perusahaan.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Nomor Telepon *</label>
                <input className="form-input" value={form.contactPhone}
                  onChange={e => up('contactPhone', e.target.value)}
                  placeholder="08xx-xxxx-xxxx" />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setStep(0)}>
                <ChevronLeft size={16} /> Lihat Panduan
              </button>
              <button className="btn btn-primary" disabled={!step1OK} onClick={() => setStep(2)}>
                Lanjut <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Materi iklan */}
        {step === 2 && (
          <div className="ki-card">
            <div className="submit-ad-card-title">Materi Iklan</div>

            {/* Pilih tipe */}
            <div className="form-group">
              <label className="form-label">Tipe Iklan *</label>
              <div className="submit-ad-type-row">
                {[
                  { val: 'image', icon: <Image size={20} />, label: 'Banner Gambar', desc: 'Unggah gambar banner horizontal' },
                  { val: 'text', icon: <Type size={20} />, label: 'Teks', desc: 'Iklan berupa teks dengan latar warna' },
                ].map(t => (
                  <button
                    key={t.val} type="button"
                    className={`submit-ad-type-card ${form.adType === t.val ? 'selected' : ''}`}
                    onClick={() => up('adType', t.val)}
                  >
                    <span className="submit-ad-type-icon">{t.icon}</span>
                    <span className="submit-ad-type-label">{t.label}</span>
                    <span className="submit-ad-type-desc">{t.desc}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Materi gambar */}
            {form.adType === 'image' && (
              <>
                <div className="form-group">
                  <label className="form-label">Gambar Banner *</label>
                  <div className="ad-guide-spec-note" style={{ marginBottom: 10 }}>
                    Resolusi minimal 1200 x 200 px, rasio 6:1, maks. 2 MB (JPG/PNG/WebP).
                  </div>
                  {form.imageUrl ? (
                    <div className="submit-ad-img-preview">
                      <img src={resolvePhoto(form.imageUrl)} alt="Preview banner" />
                      <button className="submit-ad-img-remove" onClick={() => up('imageUrl', '')}>
                        <X size={14} /> Hapus dan unggah ulang
                      </button>
                    </div>
                  ) : (
                    <label className="submit-ad-upload-zone">
                      <Upload size={24} />
                      <span>{uploading ? 'Mengunggah...' : 'Klik untuk unggah gambar'}</span>
                      <span className="submit-ad-upload-hint">JPG, PNG, WebP, maks. 2 MB</span>
                      <input ref={fileRef} type="file" accept="image/*" hidden onChange={uploadImage} disabled={uploading} />
                    </label>
                  )}
                </div>
                <div className="form-group">
                  <label className="form-label">Teks Alternatif Gambar</label>
                  <input className="form-input" value={form.imageAlt}
                    onChange={e => up('imageAlt', e.target.value)}
                    placeholder="Deskripsi singkat gambar untuk aksesibilitas" />
                </div>
              </>
            )}

            {/* Materi teks */}
            {form.adType === 'text' && (
              <>
                <div className="form-group">
                  <label className="form-label">Judul Iklan</label>
                  <input className="form-input" value={form.title}
                    onChange={e => up('title', e.target.value)}
                    placeholder="Judul singkat (maks. 60 karakter)"
                    maxLength={60} />
                  <div className="submit-ad-char-count">{form.title.length}/60</div>
                </div>
                <div className="form-group">
                  <label className="form-label">Teks Iklan *</label>
                  <textarea className="form-textarea" rows={3} value={form.bodyText}
                    onChange={e => up('bodyText', e.target.value)}
                    placeholder="Teks iklan yang akan ditampilkan (maks. 120 karakter)"
                    maxLength={120} />
                  <div className="submit-ad-char-count">{form.bodyText.length}/120</div>
                </div>
              </>
            )}

            {/* URL tujuan */}
            <div className="submit-ad-grid-2">
              <div className="form-group">
                <label className="form-label">URL Tujuan Iklan *</label>
                <input className="form-input" value={form.linkUrl}
                  onChange={e => up('linkUrl', e.target.value)}
                  placeholder="https://websiteanda.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Label Tautan (khusus iklan teks)</label>
                <input className="form-input" value={form.linkLabel}
                  onChange={e => up('linkLabel', e.target.value)}
                  placeholder="Contoh: Pelajari Lebih Lanjut" />
              </div>
            </div>

            {/* Periode tayang */}
            <div className="submit-ad-grid-2">
              <div className="form-group">
                <label className="form-label">Tanggal Mulai Tayang *</label>
                <input className="form-input" type="date" value={form.startDate}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={e => up('startDate', e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Tanggal Selesai Tayang *</label>
                <input className="form-input" type="date" value={form.endDate}
                  min={form.startDate || new Date().toISOString().split('T')[0]}
                  onChange={e => up('endDate', e.target.value)} />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Catatan Tambahan (opsional)</label>
              <textarea className="form-textarea" rows={2} value={form.notes}
                onChange={e => up('notes', e.target.value)}
                placeholder="Informasi tambahan untuk tim redaksi..." />
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8 }}>
              <button className="btn btn-ghost" onClick={() => setStep(1)}>
                <ChevronLeft size={16} /> Kembali
              </button>
              <button className="btn btn-primary" disabled={!step2OK} onClick={() => setStep(3)}>
                Pratinjau <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Konfirmasi */}
        {step === 3 && (
          <div className="ki-card">
            <div className="submit-ad-card-title">Konfirmasi Pengajuan</div>

            <div className="submit-ad-preview-box">
              <div className="submit-ad-preview-section">Data Pengiklan</div>
              {[
                ['Perusahaan', form.companyName],
                ['Kontak', form.contactName],
                ['Email', form.contactEmail],
                ['Telepon', form.contactPhone],
              ].map(([l, v]) => (
                <div key={l} className="submit-ad-preview-row">
                  <span className="submit-ad-preview-label">{l}</span>
                  <span className="submit-ad-preview-val">{v}</span>
                </div>
              ))}

              <div className="submit-ad-preview-section" style={{ marginTop: 16 }}>Materi Iklan</div>
              {[
                ['Tipe', form.adType === 'image' ? 'Banner Gambar' : 'Teks'],
                ...(form.adType === 'text' ? [['Teks', form.bodyText]] : []),
                ['URL Tujuan', form.linkUrl],
                ...(form.linkLabel ? [['Label Tautan', form.linkLabel]] : []),
                ['Mulai Tayang', form.startDate],
                ['Selesai Tayang', form.endDate],
              ].map(([l, v]) => (
                <div key={l} className="submit-ad-preview-row">
                  <span className="submit-ad-preview-label">{l}</span>
                  <span className="submit-ad-preview-val">{v}</span>
                </div>
              ))}

              {form.adType === 'image' && form.imageUrl && (
                <div style={{ marginTop: 12 }}>
                  <div className="submit-ad-preview-label" style={{ marginBottom: 8 }}>Pratinjau Banner</div>
                  <img src={resolvePhoto(form.imageUrl)} alt="Banner" className="submit-ad-confirm-img" />
                </div>
              )}
            </div>

            <div className="alert alert-info" style={{ marginTop: 16 }}>
              Setelah dikirim, tim redaksi akan meninjau pengajuan Anda dalam 1 sampai 3 hari kerja
              dan menghubungi Anda melalui email yang tercantum.
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button className="btn btn-ghost" onClick={() => setStep(2)} disabled={saving}>
                <ChevronLeft size={16} /> Kembali
              </button>
              <button className="btn btn-primary" style={{ flex: 1 }} onClick={submit} disabled={saving}>
                {saving ? 'Mengirim...' : 'Kirim Pengajuan'}
              </button>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
